/*
 * Cart Service
 * ------------
 * The single entry point for every cart read and mutation in the theme.
 *
 * Why it exists:
 *  - Reads are deduped and cached, so the many independent `/cart.js` callers
 *    share one request instead of each firing their own.
 *  - The cart is warmed the moment this script runs. It is loaded non-deferred
 *    in <head>, so the request is already in flight during HTML parsing.
 *  - Mutations (add / update / change / clear) run through a single queue, so
 *    two cart writes can never race each other.
 *  - update / change / clear return the full cart, so their response seeds the
 *    cache directly — no extra `/cart.js` is needed. `add` is the one exception:
 *    the Ajax API returns only the added line(s), not the cart, so `add` is
 *    followed by exactly one refresh.
 *
 * Public API (window.JPCartService):
 *   getCart({ force })  -> Promise<cart>            deduped read (force bypasses cache)
 *   refresh()           -> Promise<cart>            force-reload + broadcast
 *   add(body)           -> Promise<{ cart, added }> body: { items:[...] } | single item | urlencoded string
 *   batchAdd(items)     -> Promise<{ cart, added }> add several items in one request, then one reload
 *   update(payload)     -> Promise<cart>            payload: { updates } | { attributes } | { note }
 *   change(payload)     -> Promise<cart>            payload: { id|line, quantity, ... }
 *   clear()             -> Promise<cart>
 *   add(body, { reload:false }) skips the post-add reload — caller refreshes itself
 *   invalidate()        -> void                     mark the cache stale (next read refetches)
 *   onChange(fn)        -> unsubscribe fn           fn(cart) on every cache change
 *   peek()              -> cart | null              last known cart, sync, no fetch
 *
 * A `cart:updated` DOM event (event.detail === cart) is also dispatched on every
 * cache change, for listeners that prefer events over onChange().
 *
 * Cross-tab: the cart is shared across the whole Shopify session, so a mutation
 * in one tab signals the others (BroadcastChannel, with a localStorage fallback).
 * Receivers invalidate their cache and — if visible — reload, dispatching a
 * `cart:invalidated` DOM event. Reads never signal; receivers never re-signal.
 *
 * All mutations run through one serial queue, so no two cart writes ever overlap
 * (in particular a clear can never race an add). Each mutation is aborted after a
 * timeout; update/change/clear are retried on transient failures (safe — they set
 * absolute quantities); add is never retried.
 *
 * Shared-cart restore: on a ?cart_token= landing, custom-cart.js clears the cart
 * and re-adds the shared items. Every mutation queued here first waits on the
 * lock armed in theme.liquid (window.awaitSharedCartRestore), so no other write
 * can be wiped by that clear or race that add. The restore's own clear/add pass
 * { gate: false } to bypass the wait — they are what releases it.
 *
 * Mutation failures reject with an Error carrying `.status` and `.body`
 * (the parsed JSON error, e.g. Shopify's 422 { message, description }).
 */
(function () {
  if (window.JPCartService) return;

  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  // A mutation (and the authoritative reload after an add) is aborted after this
  // long so a hung request can't wedge the queue. Idempotent mutations are then
  // retried on transient failures.
  var TIMEOUT_MS = 15000;
  var RETRY_ATTEMPTS = 2;      // retries after the first attempt (up to 3 tries)
  var RETRY_BASE_DELAY = 400;  // ms, doubled each retry

  function routes() {
    return (window.KROWN && KROWN.settings && KROWN.settings.routes) || {};
  }

  function isV2Enabled() {
    return !!(window.KROWN && KROWN.settings && KROWN.settings.shared_cart && KROWN.settings.shared_cart.v2_enabled);
  }

  // Locale-aware root, e.g. "/" or "/en-gb/". Prefer Shopify's own value; fall
  // back to deriving it from the localized cart_url ("/en-gb/cart" -> "/en-gb/").
  function root() {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root;
    }
    var cartUrl = routes().cart_url;
    if (cartUrl) return cartUrl.replace(/cart\/?$/, '');
    return '/';
  }

  function getUrl() { return root() + 'cart.js'; }
  function mutationUrl(routeKey, fallbackPath) {
    var base = routes()[routeKey];
    return base ? base + '.js' : root() + fallbackPath;
  }

  var current = null;             // Promise<cart> | null — the cached cart
  var lastCart = null;            // last resolved cart, for peek()
  var generation = 0;            // bumped on every committed write; guards stale reads
  var queue = Promise.resolve();  // mutation serialization chain
  var subscribers = [];

  function broadcast(cart) {
    subscribers.forEach(function (fn) {
      try { fn(cart); } catch (e) { /* a bad subscriber must not break the rest */ }
    });
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (e) { /* dispatch can fail very early in parse; ignore */ }
  }

  function parseBody(response) {
    return response.text().then(function (text) {
      if (!text) return null;
      try { return JSON.parse(text); } catch (e) { return null; }
    });
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // fetch() that aborts after timeoutMs.
  function fetchWithTimeout(url, options, timeoutMs) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, timeoutMs || TIMEOUT_MS);
    var opts = Object.assign({}, options, { signal: controller.signal });
    return fetch(url, opts).then(
      function (response) { clearTimeout(timer); return response; },
      function (error) { clearTimeout(timer); throw error; }
    );
  }

  function fetchCart() {
    return fetchWithTimeout(getUrl(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (response) {
      if (!response.ok) throw new Error('cart.js ' + response.status);
      return response.json();
    });
  }

  // Fetch and store as the cache. A read that resolves after a newer write
  // committed is dropped (generation guard) so it can't clobber fresh state.
  function loadIntoCache() {
    var gen = generation;
    var pending = fetchCart();
    if (current === null) current = pending;
    pending.then(function (cart) {
      if (gen !== generation) return;
      lastCart = cart;
      current = Promise.resolve(cart);
    }, function () {
      if (current === pending) current = null;
    });
    return pending;
  }

  // Replace the cache with a cart returned by a mutation, and broadcast.
  function seed(cart) {
    generation++;
    current = Promise.resolve(cart);
    lastCart = cart;
    broadcast(cart);
    notifyOtherTabs();
    return cart;
  }

  function getCart(options) {
    if (options && options.force) return loadIntoCache();
    return current || loadIntoCache();
  }

  function postOnce(url, body, headers) {
    var options = { method: 'POST', credentials: 'same-origin' };
    if (typeof body === 'string') {
      options.headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      };
      options.body = body;
    } else {
      options.headers = JSON_HEADERS;
      options.body = JSON.stringify(body || {});
    }
    if (headers) options.headers = Object.assign({}, options.headers, headers);
    return fetchWithTimeout(url, options, TIMEOUT_MS);
  }

  // Retry transient failures (network error / abort / >= 500) with exponential
  // backoff. Safe ONLY for idempotent mutations (update / change / clear), which
  // set absolute quantities — never for add, where a retry could add the item
  // twice. 4xx is returned as-is (bad request data, not a transient fault).
  function postWithRetry(url, body, headers) {
    function attempt(n) {
      return postOnce(url, body, headers).then(function (response) {
        if (response.status >= 500 && n < RETRY_ATTEMPTS) {
          return delay(RETRY_BASE_DELAY * Math.pow(2, n)).then(function () { return attempt(n + 1); });
        }
        return response;
      }, function (error) {
        if (n < RETRY_ATTEMPTS) {
          return delay(RETRY_BASE_DELAY * Math.pow(2, n)).then(function () { return attempt(n + 1); });
        }
        throw error;
      });
    }
    return attempt(0);
  }

  // --- Shared-cart restore gate ------------------------------------------------
  // A shared-cart link (?cart_token=...) makes custom-cart.js clear the cart and
  // re-add the shared items. Any other write landing in that window is either
  // wiped by the clear or races the add, so every mutation waits for the restore
  // to finish first. theme.liquid arms the lock and exposes
  // awaitSharedCartRestore(); we read it lazily on each call because this script
  // runs in <head>, before that inline block defines it.
  function awaitRestoreGate() {
    var wait = window.awaitSharedCartRestore;
    if (typeof wait !== 'function') return Promise.resolve();
    try {
      return Promise.resolve(wait());
    } catch (e) {
      return Promise.resolve();
    }
  }

  function enqueueNow(task) {
    var run = queue.then(task);
    queue = run.then(function () {}, function () {});
    return run;
  }

  // Run a task after all previously queued mutations settle. A rejected task
  // must not break the chain for mutations queued behind it.
  //
  // The restore gate is awaited BEFORE joining the queue, never inside the slot.
  // Waiting inside would park a gated mutation in the middle of the chain, and
  // the restore's own clear/add — queued behind it — could then never run to
  // release the gate. Restore writes themselves pass { gate: false }.
  function enqueue(task, options) {
    if (options && options.gate === false) return enqueueNow(task);
    return awaitRestoreGate().then(function () { return enqueueNow(task); });
  }

  function mutationError(response, body) {
    var error = new Error('cart mutation failed: ' + response.status);
    error.status = response.status;
    error.body = body;
    return error;
  }

  // update / change / clear each return the full cart -> seed the cache from it.
  // Retried on transient failures (idempotent).
  function fullCartMutation(url, body, options) {
    return enqueue(function () {
      return postWithRetry(url, body, options && options.headers).then(function (response) {
        return parseBody(response).then(function (cart) {
          if (!response.ok) throw mutationError(response, cart);
          return seed(cart);
        });
      });
    }, options);
  }

  // Mark the cached cart stale so the next read re-fetches. Use after a mutation
  // whose fresh state you don't need to seed right now (or that you refresh
  // yourself), so we don't do a redundant reload.
  function invalidate() {
    generation++;
    current = null;
  }

  var localRefreshTimer = null;

  function scheduleLocalRefresh() {
    if (!isV2Enabled()) return;
    if (localRefreshTimer) clearTimeout(localRefreshTimer);
    localRefreshTimer = setTimeout(function () {
      localRefreshTimer = null;
      reloadAndBroadcast().catch(function () {});
    }, 120);
  }

  // add returns only the added line(s), not the cart, so its response is never
  // trusted for cart state. By default we reload the authoritative cart after.
  // Pass { reload: false } when the caller refreshes cart state itself (e.g.
  // renders the cart section + calls refreshCartPaymentMethods) — then we only
  // invalidate the cache, avoiding a duplicate reload. Not retried: a retry
  // could double-add.
  function add(body, options) {
    var reload = !options || options.reload !== false;
    return enqueue(function () {
      return postOnce(mutationUrl('cart_add_url', 'cart/add.js'), body, options && options.headers).then(function (response) {
        return parseBody(response).then(function (added) {
          if (!response.ok) throw mutationError(response, added);
          if (!reload) {
            invalidate();
            notifyOtherTabs();
            scheduleLocalRefresh();
            return { cart: null, added: added };
          }
          return fetchCart().then(function (cart) {
            seed(cart);
            return { cart: cart, added: added };
          });
        });
      });
    }, options);
  }

  // Add several items in a single /cart/add.js request, then one reload. Use
  // this instead of multiple add() calls when adding more than one item.
  function batchAdd(items, options) {
    var list = Array.isArray(items) ? items : [items];
    return add({ items: list }, options);
  }

  // Low-level: run a raw POST through the serial mutation queue (so it can't race
  // add/update/clear) with a timeout, and return the raw Response for callers
  // that parse it themselves — e.g. flows that render the cart section HTML and
  // handle their own 422 UI. Marks the cache stale afterwards. Not retried; use
  // for add-like calls where the caller refreshes cart state itself.
  function enqueuePost(url, body, headers, options) {
    return enqueue(function () {
      var fetchOptions = { method: 'POST', credentials: 'same-origin' };
      if (headers) fetchOptions.headers = headers;
      if (typeof body !== 'undefined' && body !== null) fetchOptions.body = body;
      return fetchWithTimeout(url, fetchOptions, TIMEOUT_MS).then(function (response) {
        invalidate();
        notifyOtherTabs();
        scheduleLocalRefresh();
        return response;
      });
    }, options);
  }

  // Easy Gift delays /cart/update.js without `updates` by 2 s; an empty map avoids it.
  function withUpdatesField(payload) {
    var body = payload || {};
    if (typeof body === 'string' || body.updates) return body;
    return Object.assign({}, body, { updates: {} });
  }

  function update(payload, options) { return fullCartMutation(mutationUrl('cart_update_url', 'cart/update.js'), withUpdatesField(payload), options); }
  function change(payload, options) { return fullCartMutation(mutationUrl('cart_change_url', 'cart/change.js'), payload || {}, options); }
  function clear(options) { return fullCartMutation(mutationUrl('cart_clear_url', 'cart/clear.js'), {}, options); }

  function onChange(fn) {
    if (typeof fn !== 'function') return function () {};
    subscribers.push(fn);
    return function () {
      var i = subscribers.indexOf(fn);
      if (i > -1) subscribers.splice(i, 1);
    };
  }

  function reloadAndBroadcast() {
    return loadIntoCache().then(function (cart) { broadcast(cart); return cart; });
  }

  // --- Cross-tab / cross-session sync -----------------------------------------
  // Shopify's cart is shared across the whole session, so a mutation in ANY tab
  // makes every other tab's cached cart stale. After each local mutation we
  // signal the other tabs; on receipt they invalidate (and, if visible, reload)
  // so no tab ever acts on a stale cart. Reads/refreshes never signal, and the
  // receiver only invalidates (never re-signals), so tabs can't ping-pong.
  var TAB_ID = Math.random().toString(36).slice(2) + '.' + Date.now().toString(36);
  var SYNC_KEY = 'jp-cart-sync';
  var syncChannel = null;
  var syncSeq = 0;
  var externalChangeTimer = null;

  function handleExternalCartChange() {
    // Drop our cache now so any read before the debounced reload is fresh.
    invalidate();
    if (externalChangeTimer) clearTimeout(externalChangeTimer);
    externalChangeTimer = setTimeout(function () {
      externalChangeTimer = null;
      try { document.dispatchEvent(new CustomEvent('cart:invalidated')); } catch (e) {}
      // Live-update visible tabs; hidden tabs stay invalidated and catch up on
      // their next read or when they become visible again.
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        reloadAndBroadcast().catch(function () {});
      }
    }, 150);
  }

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      syncChannel = new BroadcastChannel(SYNC_KEY);
      syncChannel.onmessage = function (event) {
        if (event && event.data && event.data.tabId !== TAB_ID) {
          handleExternalCartChange();
        }
      };
    } catch (e) { syncChannel = null; }
  }
  if (typeof window !== 'undefined' && window.addEventListener) {
    // `storage` fires only in OTHER tabs — the fallback when BroadcastChannel is
    // unavailable. Guarded so we never double-handle when the channel is active.
    window.addEventListener('storage', function (event) {
      if (!syncChannel && event.key === SYNC_KEY && event.newValue) {
        handleExternalCartChange();
      }
    });
    // A tab mutated while we were hidden won't have live-refreshed us; catch up
    // when we become visible again if our cache is still stale.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && current === null) {
        reloadAndBroadcast().catch(function () {});
      }
    });
  }

  // Signal other tabs that this tab just mutated the shared cart. Called only
  // from local mutation commits — never from the receiver above.
  function notifyOtherTabs() {
    if (syncChannel) {
      try { syncChannel.postMessage({ tabId: TAB_ID, seq: ++syncSeq }); return; } catch (e) { /* fall through */ }
    }
    try { localStorage.setItem(SYNC_KEY, TAB_ID + ':' + (++syncSeq)); } catch (e) {}
  }

  window.JPCartService = {
    getCart: getCart,
    refresh: reloadAndBroadcast,
    add: add,
    batchAdd: batchAdd,
    update: update,
    change: change,
    clear: clear,
    enqueuePost: enqueuePost,
    invalidate: invalidate,
    onChange: onChange,
    peek: function () { return lastCart; }
  };

  // Warm the cart right now — this script is non-deferred in <head>, so the
  // request goes out during HTML parsing, before any component needs it.
  loadIntoCache().then(broadcast, function () {});
})();
